/**
 * WizardStepConfigure — Step 2 of the New Transformation wizard.
 * Accordion of all sources with column selection and data preview.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  AttachFile,
  Cable,
} from '@mui/icons-material';
import type { ConnectorSourceEntry } from './ConnectorSourcePicker';
import type { FilePreviewResponse } from '../../services/batchApi';
import { previewFile } from '../../services/batchApi';
import { discoverTables, previewTable, previewConnectorFile } from '../../services/connectorApi';
import type { DiscoveredTable } from '../../services/connectorApi';

/** Column info from either file preview or connector discovery. */
interface ColumnInfo {
  name: string;
  type: string;
}

/** Per-source configuration state. */
interface SourceConfig {
  columns: ColumnInfo[];
  selectedColumns: string[];
  previewRows: any[][];
  previewColumnNames: string[];
  loading: boolean;
  error: string | null;
  /** true when this is a connector Excel entry waiting for user to expand before downloading */
  needsExcelLoad?: boolean;
  /** true when this entry is one sheet of a multi-sheet connector Excel */
  isSheet?: boolean;
  /** display label for a connector Excel sheet, e.g. "Sheet1" */
  sheetDisplayName?: string;
}

// Key builders
const fileKey = (file: File) => `file::${file.name}::${file.size}`;
const connKey = (entry: ConnectorSourceEntry) => entry.key;

/** Show API detail on Step 2 errors (same idea as submit on Step 3). */
const formatApiError = (error: unknown, fallback: string): string => {
  const respDetail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof respDetail === 'string' && respDetail.trim()) return respDetail;
  const respMessage = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  if (typeof respMessage === 'string' && respMessage.trim()) return respMessage;
  const msg = (error as { message?: unknown })?.message;
  if (typeof msg === 'string' && msg.trim() && !/^Request failed with status code \d+$/.test(msg)) {
    return msg;
  }
  return fallback;
};

interface WizardStepConfigureProps {
  files: File[];
  connectorSources: ConnectorSourceEntry[];
  /** Map from source key to selected column names. */
  columnSelections: Map<string, string[]>;
  onColumnSelectionsChange: React.Dispatch<React.SetStateAction<Map<string, string[]>>>;
  /** Reports whether the user may proceed past this step (no source still loading or failed to parse). */
  onValidityChange?: (valid: boolean) => void;
}

const WizardStepConfigure: React.FC<WizardStepConfigureProps> = ({
  files,
  connectorSources,
  columnSelections,
  onColumnSelectionsChange,
  onValidityChange,
}) => {
  const [configs, setConfigs] = useState<Map<string, SourceConfig>>(new Map());
  const [expanded, setExpanded] = useState<string | false>(false);

  // Block "Next" while any source is still loading or failed to parse —
  // otherwise the user could proceed with a 0-column source (e.g. an
  // unsupported file encoding) into the rest of the wizard.
  useEffect(() => {
    const totalExpected = files.length + connectorSources.length;
    const loadedConfigs = Array.from(configs.values());
    const stillResolving = loadedConfigs.length < totalExpected || loadedConfigs.some((cfg) => cfg.loading);
    const hasError = loadedConfigs.some((cfg) => cfg.error);
    const hasSelection = Array.from(columnSelections.values()).some((cols) => cols.length > 0);
    onValidityChange?.(!stillResolving && !hasError && hasSelection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configs, files.length, connectorSources.length, columnSelections]);

  // Load file preview data on mount
  useEffect(() => {
    files.forEach((file) => {
      const key = fileKey(file);
      if (configs.has(key)) return;

      // Mark loading
      setConfigs((prev) => {
        const next = new Map(prev);
        next.set(key, {
          columns: [],
          selectedColumns: [],
          previewRows: [],
          previewColumnNames: [],
          loading: true,
          error: null,
        });
        return next;
      });

      previewFile(file, 10)
        .then((resp: FilePreviewResponse) => {
          if (resp.is_multi_sheet && resp.sheets?.length) {
            // Multi-sheet Excel: create a config entry per sheet
            setConfigs((prev) => {
              const next = new Map(prev);
              for (const sheet of resp.sheets!) {
                const sheetKey = `${key}::${sheet.sheet_name}`;
                const allCols = sheet.columns.map((c) => c.name);
                next.set(sheetKey, {
                  columns: sheet.columns,
                  selectedColumns: columnSelections.get(sheetKey) || allCols,
                  previewRows: sheet.rows,
                  previewColumnNames: sheet.columns.map((c) => c.name),
                  loading: false,
                  error: null,
                });
              }
              // Remove the loading placeholder for the parent file key
              next.delete(key);
              return next;
            });
            onColumnSelectionsChange((prevSelections) => {
              const newSelections = new Map(prevSelections);
              for (const sheet of resp.sheets!) {
                const sheetKey = `${key}::${sheet.sheet_name}`;
                if (!newSelections.has(sheetKey)) {
                  newSelections.set(sheetKey, sheet.columns.map((c) => c.name));
                }
              }
              return newSelections;
            });
          } else {
            // Single-sheet file
            const columns = resp.columns || [];
            const allCols = columns.map((c) => c.name);
            setConfigs((prev) => {
              const next = new Map(prev);
              next.set(key, {
                columns,
                selectedColumns: columnSelections.get(key) || allCols,
                previewRows: resp.rows || [],
                previewColumnNames: columns.map((c) => c.name),
                loading: false,
                error: null,
              });
              return next;
            });
            onColumnSelectionsChange((prevSelections) => {
              if (prevSelections.has(key)) return prevSelections;
              const newSelections = new Map(prevSelections);
              newSelections.set(key, allCols);
              return newSelections;
            });
          }
        })
        .catch((e: any) => {
          setConfigs((prev) => {
            const next = new Map(prev);
            next.set(key, {
              columns: [],
              selectedColumns: [],
              previewRows: [],
              previewColumnNames: [],
              loading: false,
              error: formatApiError(e, 'Failed to preview file'),
            });
            return next;
          });
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  // Load connector table column metadata on mount
  useEffect(() => {
    connectorSources.forEach((entry) => {
      const key = connKey(entry);
      // Skip if already loaded (or already expanded into sheets)
      if (configs.has(key) || Array.from(configs.keys()).some((k) => k.startsWith(`${key}::`))) return;

      // Excel files from filesystem connectors: auto-load sheet metadata immediately,
      // same behaviour as local Excel file uploads.
      if (/\.(xlsx|xls)$/i.test(entry.tableName)) {
        setConfigs((prev) => {
          const next = new Map(prev);
          next.set(key, {
            columns: [], selectedColumns: [], previewRows: [], previewColumnNames: [],
            loading: true, error: null,
          });
          return next;
        });
        loadConnectorExcel(key, entry);
        return;
      }

      setConfigs((prev) => {
        const next = new Map(prev);
        next.set(key, {
          columns: [],
          selectedColumns: entry.selectedColumns || [],
          previewRows: [],
          previewColumnNames: [],
          loading: true,
          error: null,
        });
        return next;
      });

      // Discover columns
      discoverTables({
        connectorId: entry.connectorId,
        search: entry.tableName,
        includeColumns: true,
        page: 1,
        pageSize: 1,
      })
        .then((resp) => {
          const match = resp.tables?.find((t: DiscoveredTable) => t.name === entry.tableName);
          const columns: ColumnInfo[] = match?.columns?.map((c) => ({
            name: c.name,
            type: c.dtype || 'string',
          })) || [];
          const allCols = columns.map((c) => c.name);
          const selected = entry.selectedColumns?.length ? entry.selectedColumns : allCols;

          // Also fetch preview
          return previewTable({
            connectorId: entry.connectorId,
            tableName: entry.tableName,
            schemaName: entry.schemaName,
            columns: selected.length > 0 ? selected : undefined,
            limit: 10,
          }).then((preview) => ({ columns, selected, preview }))
            .catch(() => ({ columns, selected, preview: null }));
        })
        .then(({ columns, selected, preview }) => {
          setConfigs((prev) => {
            const next = new Map(prev);
            next.set(key, {
              columns,
              selectedColumns: columnSelections.get(key) || selected,
              previewRows: preview?.rows || [],
              previewColumnNames: preview?.columns?.map((c: any) => typeof c === 'string' ? c : c.name) || columns.map((c) => c.name),
              loading: false,
              error: null,
            });
            return next;
          });
          onColumnSelectionsChange((prevSelections) => {
            if (prevSelections.has(key)) return prevSelections;
            const newSelections = new Map(prevSelections);
            newSelections.set(key, selected);
            return newSelections;
          });
        })
        .catch((e: any) => {
          setConfigs((prev) => {
            const next = new Map(prev);
            next.set(key, {
              columns: [],
              selectedColumns: [],
              previewRows: [],
              previewColumnNames: [],
              loading: false,
              error: formatApiError(e, 'Failed to discover columns'),
            });
            return next;
          });
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectorSources]);

  const handleColumnToggle = useCallback(
    (sourceKey: string, columnName: string) => {
      setConfigs((prev) => {
        const next = new Map(prev);
        const cfg = next.get(sourceKey);
        if (!cfg) return prev;
        const isSelected = cfg.selectedColumns.includes(columnName);
        const updated = isSelected
          ? cfg.selectedColumns.filter((c) => c !== columnName)
          : [...cfg.selectedColumns, columnName];
        next.set(sourceKey, { ...cfg, selectedColumns: updated });
        onColumnSelectionsChange((prevSelections) => {
          const newSelections = new Map(prevSelections);
          newSelections.set(sourceKey, updated);
          return newSelections;
        });
        return next;
      });
    },
    [onColumnSelectionsChange],
  );

  const handleSelectAll = useCallback(
    (sourceKey: string) => {
      setConfigs((prev) => {
        const next = new Map(prev);
        const cfg = next.get(sourceKey);
        if (!cfg) return prev;
        const allSelected = cfg.selectedColumns.length === cfg.columns.length;
        const updated = allSelected ? [] : cfg.columns.map((c) => c.name);
        next.set(sourceKey, { ...cfg, selectedColumns: updated });
        onColumnSelectionsChange((prevSelections) => {
          const newSelections = new Map(prevSelections);
          newSelections.set(sourceKey, updated);
          return newSelections;
        });
        return next;
      });
    },
    [onColumnSelectionsChange],
  );

  const handleSourceToggle = useCallback(
    (sourceKey: string, check: boolean) => {
      setConfigs((prev) => {
        const next = new Map(prev);
        const cfg = next.get(sourceKey);
        if (!cfg) return prev;
        const updated = check ? cfg.columns.map((c) => c.name) : [];
        next.set(sourceKey, { ...cfg, selectedColumns: updated });
        onColumnSelectionsChange((prevSelections) => {
          const newSelections = new Map(prevSelections);
          newSelections.set(sourceKey, updated);
          return newSelections;
        });
        return next;
      });
      if (!check && expanded === sourceKey) {
        setExpanded(false); // collapse if unchecking
      }
    },
    [onColumnSelectionsChange, expanded],
  );

  const loadConnectorExcel = useCallback((key: string, entry: ConnectorSourceEntry) => {
    setConfigs((prev) => {
      const cfg = prev.get(key);
      if (!cfg) return prev;
      // Already loading or loaded (sheets expanded) — skip
      if (cfg.loading || (!cfg.needsExcelLoad && cfg.columns.length > 0)) return prev;
      const next = new Map(prev);
      next.set(key, { ...cfg, loading: true, needsExcelLoad: false });
      return next;
    });

    previewConnectorFile(entry.connectorId, entry.tableName)
      .then((resp) => {
        if (resp.is_multi_sheet && resp.sheets?.length) {
          const stem = entry.tableName.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
          setConfigs((prev) => {
            const next = new Map(prev);
            next.delete(key);
            for (const sheet of resp.sheets!) {
              const sheetTableName = `${stem}__${sheet.sheet_name}`;
              const sheetKey = `${key}::${sheetTableName}`;
              const allCols = sheet.columns.map((c) => c.name);
              next.set(sheetKey, {
                columns: sheet.columns,
                selectedColumns: columnSelections.get(sheetKey) ?? allCols,
                previewRows: sheet.rows,
                previewColumnNames: allCols,
                loading: false, error: null,
                isSheet: true, sheetDisplayName: sheet.sheet_name,
              });
            }
            return next;
          });
          onColumnSelectionsChange((prevSelections) => {
            const newSelections = new Map(prevSelections);
            for (const sheet of resp.sheets!) {
              const stem = entry.tableName.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
              const sheetKey = `${key}::${stem}__${sheet.sheet_name}`;
              if (!newSelections.has(sheetKey)) {
                newSelections.set(sheetKey, sheet.columns.map((c) => c.name));
              }
            }
            return newSelections;
          });
        } else {
          const columns = (resp.columns || []).map((c) => ({ name: c.name, type: c.type }));
          const allCols = columns.map((c) => c.name);
          setConfigs((prev) => {
            const next = new Map(prev);
            next.set(key, {
              columns, selectedColumns: columnSelections.get(key) ?? allCols,
              previewRows: resp.rows || [], previewColumnNames: allCols,
              loading: false, error: null,
            });
            return next;
          });
          onColumnSelectionsChange((prevSelections) => {
            if (prevSelections.has(key)) return prevSelections;
            const newSelections = new Map(prevSelections);
            newSelections.set(key, allCols);
            return newSelections;
          });
        }
      })
      .catch((e: any) => {
        setConfigs((prev) => {
          const next = new Map(prev);
          next.set(key, {
            columns: [], selectedColumns: [], previewRows: [], previewColumnNames: [],
            loading: false, error: formatApiError(e, 'Failed to preview Excel file'),
          });
          return next;
        });
      });
  }, [columnSelections, onColumnSelectionsChange]);

  const renderSourcePanel = (key: string, label: string, icon: React.ReactNode, isFile: boolean) => {
    const cfg = configs.get(key);
    const isExcelSheet = (isFile && key.split('::').length > 3) || !!cfg?.isSheet;

    return (
      <Accordion
        key={key}
        expanded={expanded === key}
        onChange={(_, isExpanded) => setExpanded(isExpanded ? key : false)}
        variant="outlined"
        sx={{ '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
            {cfg && !cfg.loading && isExcelSheet && (
              <Checkbox
                size="small"
                checked={cfg.selectedColumns.length > 0}
                onChange={(e) => {
                  e.stopPropagation();
                  handleSourceToggle(key, e.target.checked);
                }}
                onClick={(e) => e.stopPropagation()}
                sx={{ p: 0.5 }}
              />
            )}
            {icon}
            <Typography
              variant="body2"
              fontWeight={500}
              noWrap
              sx={{
                flex: 1,
                ...(cfg && !cfg.loading && cfg.selectedColumns.length === 0
                  ? { color: 'text.disabled', textDecoration: 'line-through' }
                  : {})
              }}
            >
              {label}
            </Typography>
            {cfg && !cfg.loading && (
              <Chip
                label={`${cfg.selectedColumns.length}/${cfg.columns.length} cols`}
                size="small"
                color={
                  cfg.columns.length === 1
                    ? 'error'
                    : cfg.selectedColumns.length > 0
                      ? 'primary'
                      : 'default'
                }
                variant="outlined"
                sx={{ fontSize: 10, height: 20 }}
              />
            )}
            {cfg?.loading && <CircularProgress size={16} />}
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {cfg?.error && (
            <Alert severity="error" sx={{ mb: 1.5, fontSize: 12 }}>
              {cfg.error}
            </Alert>
          )}

          {cfg && !cfg.loading && cfg.columns.length > 0 && (
            <Box>
              {/* Column selection header */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Columns
                </Typography>
                <Button
                  size="small"
                  onClick={() => handleSelectAll(key)}
                  sx={{ fontSize: 11, textTransform: 'none', minWidth: 'auto' }}
                >
                  {cfg.selectedColumns.length === cfg.columns.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Stack>

              {/* Column checkboxes */}
              <Box sx={{ maxHeight: 180, overflow: 'auto', mb: 2 }}>
                {cfg.columns.map((col) => (
                  <FormControlLabel
                    key={col.name}
                    control={
                      <Checkbox
                        size="small"
                        checked={cfg.selectedColumns.includes(col.name)}
                        onChange={() => handleColumnToggle(key, col.name)}
                        sx={{ py: 0.25 }}
                      />
                    }
                    label={
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {col.name}
                        </Typography>
                        <Chip
                          label={col.type}
                          size="small"
                          variant="outlined"
                          sx={{ height: 16, fontSize: 9 }}
                        />
                      </Stack>
                    }
                    sx={{ display: 'flex', m: 0, width: '100%' }}
                  />
                ))}
              </Box>

              {/* Data preview */}
              {cfg.previewRows.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                    Preview ({cfg.previewRows.length} rows)
                  </Typography>
                  <TableContainer sx={{ maxHeight: 200, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {cfg.previewColumnNames
                            .filter((col) => cfg.selectedColumns.includes(col))
                            .map((col) => (
                              <TableCell
                                key={col}
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: 11,
                                  fontWeight: 600,
                                  py: 0.5,
                                  px: 1,
                                  bgcolor: 'background.default',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {col}
                              </TableCell>
                            ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cfg.previewRows.map((row, rowIdx) => {
                          // Filter to selected columns only
                          const selectedIndices = cfg.previewColumnNames
                            .map((col, idx) => ({ col, idx }))
                            .filter(({ col }) => cfg.selectedColumns.includes(col));
                          // Rows can be arrays (file preview) or objects (connector preview)
                          const isArrayRow = Array.isArray(row);
                          return (
                            <TableRow key={rowIdx}>
                              {selectedIndices.map(({ col, idx }) => {
                                const cellValue = isArrayRow ? row[idx] : (row as any)?.[col];
                                return (
                                  <TableCell
                                    key={idx}
                                    sx={{
                                      fontSize: 11,
                                      py: 0.25,
                                      px: 1,
                                      whiteSpace: 'nowrap',
                                      maxWidth: 200,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    {cellValue == null ? (
                                      <Typography component="span" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: 11 }}>
                                        null
                                      </Typography>
                                    ) : (
                                      String(cellValue)
                                    )}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}

          {cfg && !cfg.loading && cfg.columns.length === 0 && !cfg.error && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No columns discovered. All data will be imported.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  const hasSelection = Array.from(columnSelections.values()).some((cols) => cols.length > 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Configure columns and preview data for each source
      </Typography>

      {!hasSelection && Array.from(columnSelections.values()).length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Please select at least one column from one source to proceed.
        </Alert>
      )}

      {files.map((file) => {
        const key = fileKey(file);
        // Check if this file was expanded into per-sheet configs (multi-sheet Excel)
        const sheetKeys = Array.from(configs.keys()).filter((k) => k.startsWith(`${key}::`));
        if (sheetKeys.length > 0) {
          // Render each sheet as a separate panel
          return sheetKeys.map((sheetKey) => {
            const sheetName = sheetKey.split('::').pop() || '';
            return renderSourcePanel(
              sheetKey,
              `${file.name} \u2192 ${sheetName}`,
              <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark', width: 24, height: 24 }}>
                <AttachFile sx={{ fontSize: 14 }} />
              </Avatar>,
              true,
            );
          });
        }
        // Single-sheet file (or still loading)
        return renderSourcePanel(
          key,
          file.name,
          <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark', width: 24, height: 24 }}>
            <AttachFile sx={{ fontSize: 14 }} />
          </Avatar>,
          true,
        );
      })}

      {connectorSources.map((entry) => {
        const baseKey = connKey(entry);
        const sheetKeys = Array.from(configs.keys()).filter((k) => k.startsWith(`${baseKey}::`));
        const connIcon = (
          <Avatar sx={{ bgcolor: 'secondary.light', color: 'secondary.dark', width: 24, height: 24 }}>
            <Cable sx={{ fontSize: 14 }} />
          </Avatar>
        );
        if (sheetKeys.length > 0) {
          return sheetKeys.map((sheetKey) => {
            const cfg = configs.get(sheetKey);
            const sheetLabel = cfg?.sheetDisplayName ?? sheetKey.split('::').pop() ?? '';
            return renderSourcePanel(sheetKey, `${entry.tableName} \u2192 ${sheetLabel}`, connIcon, false);
          });
        }
        return renderSourcePanel(baseKey, `${entry.connectorName} \u2192 ${entry.tableName}`, connIcon, false);
      })}
    </Box>
  );
};

export default WizardStepConfigure;
export { fileKey, connKey };
